/**
 *
 * @module index
 * @author shojib [mahmud.hossain@mlb.com]
 * @description build entry point / g5-component reference
 *
 */

import G5Component from 'g5-component';
import { inject } from 'g5-component/src/scripts/dependencies/defaultInjector'; // Handlebars
import eventGroup from 'g5-component/src/scripts/events/group/group';
import util from 'util';
import flipDate from 'flipDate';
import pageMeta from 'pageMeta';
import assign from 'lodash-es/assign';
import get from 'lodash-es/get';
import MODES_USING_SPRITE from './../json/modes-using-sprite.json';
import component from './component/master';
import extender from './component/extender';
import hbHelpers from './helpers/hbHelpers';
import ScoresModel from './model/scores';
import ScoresViewModel from './viewModel/master';
import ScoresEventTower from './events/master';
import eventGroupExtender from './events/group/extender';

/**
 *
 * @name ScoresCollection
 *
 */
class ScoresCollection extends G5Component {

    /**
     *
     * @param {object} _opts shared.
     *
     */
    constructor(_opts) {

        let opts = assign(ScoresCollection.defaultOptions(), _opts);

        const impl = inject({});

        impl.component = component;
        impl.template = {};
        impl.extender = extender;
        impl.helpers = hbHelpers;
        impl.partials = {};

        impl.eventGroup = eventGroup;
        impl.eventGroupExtender = eventGroupExtender;

        impl.Model = ScoresModel;
        impl.ViewModel = ScoresViewModel;
        impl.EventTower = ScoresEventTower;

        if (opts.mode === 'MIG') {
            opts.LazyLoadMIG = {};
        } else if (opts.mode === 'minisb') {
            opts.postseasonPush = (typeof opts.postseasonPush === 'string' && opts.postseasonPush.length > 0)
                ? JSON.parse(opts.postseasonPush) : opts.postseasonPush;
            opts.isPostseasonPush = get(opts, 'postseasonPush.postseasonPush_truefalse') === 'Y';
            opts.showPostseasonPushSponsorLogo = get(opts, 'postseasonPush.postseasonPushSponsorLogo') === 'Y';
            opts.ppSponsorURL = get(opts, 'postseasonPush.ppSponsorURL', '');
        }

        opts = ScoresCollection.updateQAParams(opts);

        super(opts, impl);

        /**
         *
         * @name opts
         * @description scores options
         *
         */
        this.opts = opts;

        /**
         *
         * @name props
         * @description scores properties
         *
         */
        this.props = {
            instance: false
        };

    }

    /**
     * @returns {object} default options copy.
     */
    static defaultOptions() {
        return {
            mode: 'responsive',
            classes: [
                ''
            ],
            dictionary: {},
            enableFetch: true,
            enablePolling: true,
            interval: 15000,
            useHttps: false,
            path: 'http://qa-statsapi.mlb.com',
            date: ScoresCollection.normalizeDateStr(flipDate.today()),
            teamId: '',
            sportId: 1,
            leagueId: '',
            sport: 'mlb',
            gamesPrev: 7,
            gamesNext: 8,
            mlbMoment: undefined,
            mlbLinker: undefined,
            defaultPromo: {},
            isPostseasonPush: false,
            internationalGameIDs: {},
            homeRunDerbyImgSrc: '',
            homeRunDerbyUrl: '',
            showPostseasonPushSponsorLogo: false,
            ppSponsorURL: '',
            youtubeMLBLink: '',
            postseasonPush: {},
            showTeamLeaders: true,
            device: 'desktop',
            gameTypes: 'E, S, R, A, F, D, L, W',
            milbSportIds: [11, 12, 13, 14, 15, 16],
            siteAssetsUri: '/node_modules/mlb-site-core/site-core',
            adFrequency: 0,
            Swiper: null,
            lang: 'en',
            liveLookInSponsor: '',
            hydratesBase: {
                team: {},
                linescore: {},
                flags: {},
                liveLookin: {},
                review: {}
            },
            hydratesResponsiveAndMIG: {
                broadcasts: {
                    all: {}
                },
                decisions: {},
                linescore: {
                    matchup: {},
                    runners: {}
                },
                person: {},
                probablePitcher: {},
                stats: {}
            },
            hydratesButtons: {
                game: {
                    content: {
                        summary: {},
                        media: {
                            epg: {}
                        }
                    },
                    tickets: {}
                },
                xrefId: {}
            },
            hydratesResponsiveOnly: {
                homeRuns: {},
                previousPlay: {},
                game: {
                    content: {
                        media: {
                            featured: {}
                        }
                    }
                }
            },
            hydratesSeriesStatus: {
                seriesStatus: {
                    'useOverride=true': {}
                }
            },
            hydrateLeaders: {
                team: {
                    leaders: {
                        showOnPreview: {
                            'leaderCategories=[homeRuns,runsBattedIn,battingAverage]': {},
                            'statGroup=[pitching,hitting]': {}
                        }
                    }
                }
            },
            hydrateBroadcasts: {
                broadcasts: {
                    all: {}
                }
            },
            hydratesPromotions: {
                game: {
                    promotions: {}
                }
            },
            hydratesCurrentTeam: {
                person: {
                    currentTeam: {}
                }
            }
        };
    }

    /**
     *
     * @method updateQAParams
     * @param {Object} opts
     * @returns {Object} opts
     * @description updates options based on query params (only in non-production)
     *
    */
    static updateQAParams(opts = {}) {

        const env = { pageMeta };

        //For security reasons, don't enable on prod
        if (env !== 'production') {

            const params = ScoresCollection.getUrlParams();

            if (params) {

                const { scoresdate, scorespollingoff } = params;

                if (scoresdate) {

                    opts.date = scoresdate;

                }

                if (scorespollingoff) {

                    opts.enablePolling = false;

                }
            }
        }

        return opts;

    }

    /**
     *
     * @method updateBaseClasses
     * @param {Object} opts
     * @returns {Object} classes
     * @description updates the default classes passed to each individual game component
     *
     */
    static updateBaseClasses(opts = {}) {

        const { classes } = opts;

        return classes;

    }

    /**
     *
     * @method updateConfig
     * @param {Object} config
     * @description updates the options
     *
     */
    updateConfig(config) {
        const { opts } = this;
        this.opts = assign(opts, config);
    }

    /**
     *
     * @method init
     * @description instantiates mlbScores instance
     *
     */
    init() {

        const { verifyRequiredOpts, updateQAParams, updateBaseClasses } = ScoresCollection;
        const { core, props, opts } = this;
        const { instance } = props;

        util.log('mlbScores - init');

        verifyRequiredOpts(opts);
        this.opts.classes = updateBaseClasses(this.opts);

        if (!instance) {

            props.instance = true;

            const { mode, siteAssetsUri } = this.opts;

            this.eventTower.init();
            this.viewModel.init(this);
            this.model.init();

            this.emit('ready', this);

        }

    }

    /**
     *
     * @function normalizeDateStr
     * @param {String} dateStr
     * @description normalizes output for compatability with API call, i.e. 2015/12/18 -> 2015-12-18
     * @returns {String}
     *
     */
    static normalizeDateStr(dateStr = '') {

        return dateStr.replace(/\//g, '-');

    }

    /**
     * @function verifyRequiredOpts
     * @description Throw an error if the required options are not passed in
     * @param opts
     */
    static verifyRequiredOpts(opts = {}) {

        const { mlbMoment, mlbLinker, mode, Swiper } = opts;

        if (!mlbMoment) {

            throw Error('mlbMoment is a required option in mlb-scores');

        }

        if (!mlbLinker) {

            throw Error('mlbLinker is a required option in mlb-scores');

        }

        if (mode === 'MIG' && !Swiper) {

            throw Error('Swiper is a required option for MIG mode of mlb-scores');

        }

    }

    /**
     * @function getUrlParams
     * @description get url params on the given page
     * @param isLower=true When true, force lowercase for simplified checks
     * @returns {Object} object with query params as keys
     */
    static getUrlParams(isLower = true) {

        const url = window.location.search;
        const getQuery = url.split('?')[1];
        const paramsStrs = getQuery && getQuery.split('&');
        const params = {};

        if (paramsStrs) {

            paramsStrs.forEach((paramStr) => {

                const paramStrArray = paramStr.split('=');
                const paramValue = paramStrArray[1];
                let paramName = paramStrArray[0];

                if (isLower) {
                    paramName = paramName.toLowerCase();
                }

                params[paramName] = paramValue === undefined ? true : paramValue;
            });
        }

        return params;
    }

}

/**
 *
 * @param {object} opts
 * @returns {ScoresCollection}
 *
 */
export default function factory(opts) {

    return new ScoresCollection(opts);

}
